// FooterQuote — a single Zen-adjacent line at the bottom of every page,
// rotating through ~40 aphorisms about fucking up. Picked fresh on every
// route change (keyed off useLocation().key), so navigating between
// pages always rolls a new quote.
//
// Tone: half real attributions (Beckett, Stevenson, Wilde, etc.), half
// originals in the project's "u done fucked up" voice. All on the theme
// of mistakes, repetition, falling, and the 12th-time-being-a-pattern
// energy that defines this cohort.

import { useMemo } from "react";
import { useLocation } from "react-router-dom";

interface Quote {
  text: string;
  /** Omit for in-house aphorisms in the project's voice. */
  attr?: string;
}

const QUOTES: Quote[] = [
  // The pattern — repeat-offender koans
  { text: "First time is an accident. Second time is a coincidence. Twelfth time is a pattern." },
  { text: "Some patterns are too consistent to be accidents." },
  { text: "Statistically, you've already done it again." },
  { text: "The simplest crime is the one you'll do twice." },
  { text: "Three speeding tickets in one summer is a personality." },
  { text: "Two DWIs are a learning curve. Three is a curriculum." },
  { text: "Some men learn from history. Others appear in it. Repeatedly." },
  { text: "The same mistake, repeated long enough, becomes a craft." },
  { text: "Practice makes permanent." },
  { text: "Routine is just a felony you've made comfortable." },

  // The system has memory
  { text: "The court remembers. The court always remembers." },
  { text: "The system has a long memory and infinite patience." },
  { text: "The bench has seen you before." },
  { text: "When the court calls your name, they don't have to look it up anymore." },
  { text: "The judge isn't surprised. The judge has never been surprised." },
  { text: "All roads lead back to the same municipal building." },
  { text: "Newark Municipal called. They miss you." },
  { text: "The clerk knows your middle name now." },
  { text: "The bailiff waved hello. That's not a good sign." },

  // Fine / warrant / time payment loop
  { text: "Disposed is just a fancy word for see you again." },
  { text: "Resolved by Time Payment is not resolved." },
  { text: "Time payment, life sentence." },
  { text: "Open warrants close nothing." },
  { text: "Even the warrant gets tired of waiting." },
  { text: "A bench warrant is just a very firm invitation." },
  { text: "Pay the fine, mute the lesson." },
  { text: "Every fine is tuition for a lesson you'll skip." },
  { text: "Indictable is a Latin word that means worth their time." },
  { text: "An unpaid surcharge is patience with interest." },

  // Driving / suspended / MVC carousel
  { text: "Suspended licenses don't suspend bad decisions." },
  { text: "Driving while suspended is its own kind of meditation." },
  { text: "Half the rap sheet is just driving home from work." },
  { text: "You cannot outrun a speeding ticket. You can only date the next one." },
  { text: "Same parking spot. Different decade. Same outcome." },
  { text: "Ten years. Same ticket. Different car." },
  { text: "Every parking ticket is a love letter from the municipality." },
  { text: "Tickets multiply when you're not looking." },
  { text: "If you cannot register the car, you cannot avoid the ticket." },
  { text: "The MVC always has another fee in its pocket." },

  // Time + inevitability
  { text: "Court date today, court date next year, court date in fifteen years." },
  { text: "Disposed today. Charged tomorrow. Court next month." },
  { text: "If at first you don't succeed, you're already on the docket." },
  { text: "He who lives by the citation, dies by the warrant." },
  { text: "Some people learn. Some people get arraigned." },
  { text: "Mercy is a luxury. The plea bargain is a craft." },
  { text: "This isn't a record. It is a routine." },
  { text: "The fines change. The choices don't." },
  { text: "Today's open case is tomorrow's old case." },
  { text: "Every clean year was paid for by a messy one." },

  // Dumb-crime zen — the rotisserie chicken energy
  { text: "If you are stuffing a rotisserie chicken under your shirt, you may want to rethink your life choices." },
  { text: "Asking the cashier where the security cameras are is, in itself, a confession." },
  { text: "Running from the officer who already knows your address never works." },
  { text: "Don't do the crime that requires the very specific tool you don't have." },
  { text: "If the plan starts with 'all I have to do is', it isn't a plan." },
  { text: "The simplest tell is wearing the stolen jersey to the arraignment." },
  { text: "Stealing from the place that knows your name is not a strategy." },
  { text: "The getaway scooter is not a getaway." },
  { text: "Posting the receipt is the part the prosecutor likes best." },
  { text: "If the only witness is your cousin, the only suspect is your cousin." },
  { text: "Buying the lottery ticket with the stolen card is a documentary." },
  { text: "Driving the stolen car to the courthouse to fight a different ticket is a haiku of self-defeat." },
  { text: "The crime you brag about at the bar is the crime you do time for." },
  { text: "Putting your name on the bag was, in retrospect, the bold choice." },
  { text: "If you can be identified by your laugh, do not laugh during the robbery." },
  { text: "Don't argue with the camera. The camera is winning." },

  // Quiet zen — the broader truth
  { text: "Every record is a long way of asking for help." },
  { text: "The arrest tells you what happened. It does not tell you who." },
  { text: "Files close. People don't." },
  { text: "Behind every charge is a Tuesday afternoon nobody saw coming." },
  { text: "The hardest part of the new chapter is closing the old one." },
  { text: "What looks like a record from the outside is a rhythm from the inside." },
  { text: "Trouble repeats, but so does the choice not to." },
  { text: "The work is small. The pattern is long. Both are true." },
];

export function FooterQuote(): JSX.Element {
  const location = useLocation();
  // Re-roll the quote on every route change so navigation always feels
  // fresh. useLocation().key changes per navigation (incl. POP via back
  // button + PUSH on link clicks), so this gives 1 quote per page-view.
  const quote = useMemo(() => {
    const i = Math.floor(Math.random() * QUOTES.length);
    return QUOTES[i] ?? QUOTES[0]!;
  }, [location.key]);

  return (
    <section
      className="no-print"
      aria-label="Reflection"
      style={{
        marginTop: 64,
        marginBottom: 16,
        paddingInline: 24,
      }}
    >
      <div
        className="container"
        style={{
          textAlign: "center",
          /* Wider so longer one-liners don't wrap prematurely on a 1440px+
             desktop. 1200px gives ~22 chars/line at 44px serif italic
             without breaking the editorial reading rhythm. */
          maxWidth: 1200,
          marginInline: "auto",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 32,
            height: 1,
            background: "rgba(255,255,255,0.28)",
            margin: "0 auto 28px",
          }}
        />
        <blockquote
          style={{
            margin: 0,
            fontFamily: "Fraunces, var(--font-serif), serif",
            fontStyle: "italic",
            fontWeight: 500,
            // Far larger than before (was 15-17px). Now scales from
            // 26px on phones up to 44px on wide desktops, in white,
            // so it actually reads as a closing line rather than a
            // dim afterthought.
            fontSize: "clamp(26px, 3.4vw, 44px)",
            lineHeight: 1.35,
            color: "white",
            fontVariationSettings: '"opsz" 96, "SOFT" 60',
            letterSpacing: "-0.01em",
          }}
        >
          {quote.text}
        </blockquote>
        {quote.attr && (
          <cite
            style={{
              display: "block",
              marginTop: 14,
              fontFamily: "var(--font-mono)",
              fontStyle: "normal",
              fontSize: 12,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.55)",
            }}
          >
            — {quote.attr}
          </cite>
        )}
      </div>
    </section>
  );
}
